/*
---------------------------------------------------------
Copyright © Daniel Woik. All rights reserved.
Unauthorized copying, modification, distribution, or use
of this code or any portion of it, is strictly prohibited.
Violators will be prosecuted to the fullest extent of the law.
---------------------------------------------------------
*/

if (!customElements.get('dw-cart-recommendations')) {
  const DW_CART_REC_DEBUG = false;

  class DwCartRecommendations extends HTMLElement {
    constructor() {
      super();
      this.currentIndex = 0;
      this.isLoading = false;
      this.retryTimer = null;
      this.observer = null;
      this.hasInteracted = false;
      this.isHovering = false;

      this.lastCartSignature = '';
      this.cartPollInterval = null;
      this.fastPollTimeout = null;

      this.drag = {
        active: false,
        startX: 0,
        currentX: 0,
        baseTranslate: 0,
        pointerId: null,
        moved: false
      };
    }

    connectedCallback() {
      this.setupObserver();
      this.startCartPolling();
      this.queueLoad();
    }

    disconnectedCallback() {
      if (this.observer) {
        this.observer.disconnect();
      }
      if (this.retryTimer) {
        clearTimeout(this.retryTimer);
      }
      if (this.fastPollTimeout) {
        clearTimeout(this.fastPollTimeout);
      }
      if (this.cartPollInterval) {
        clearInterval(this.cartPollInterval);
      }
      this.teardownSwipe();
    }

    debug(...args) {
      if (DW_CART_REC_DEBUG) {
        console.log('[DW Cart Recommendations]', ...args);
      }
    }

    setupObserver() {
      if (this.observer) {
        this.observer.disconnect();
      }

      this.observer = new MutationObserver((mutations) => {
        let shouldReload = false;

        for (const mutation of mutations) {
          if (mutation.type === 'attributes') {
            if (
              mutation.attributeName === 'data-product-id' ||
              mutation.attributeName === 'data-intent' ||
              mutation.attributeName === 'data-limit' ||
              mutation.attributeName === 'data-max-price' ||
              mutation.attributeName === 'data-enable-max-price' ||
              mutation.attributeName === 'data-hide-sold-out'
            ) {
              shouldReload = true;
              break;
            }
          }

          if (mutation.type === 'childList') {
            const track = this.querySelector('.dw-cart-recommendations__track');
            const hasCards = track && track.querySelector('.dw-cart-recommendations__card');

            if (!hasCards && this.hasUsableProductId()) {
              shouldReload = true;
              break;
            }
          }
        }

        if (shouldReload) {
          this.debug('MutationObserver triggered reload');
          this.queueLoad(50);
        }
      });

      this.observer.observe(this, {
        attributes: true,
        childList: true,
        subtree: true
      });
    }

    queueLoad(delay = 0) {
      if (this.retryTimer) {
        clearTimeout(this.retryTimer);
      }

      this.retryTimer = setTimeout(() => {
        this.loadRecommendations();
      }, delay);
    }

    hasUsableProductId() {
      return (this.dataset.productId || '').trim() !== '';
    }

    async fetchCart() {
      const response = await fetch('/cart.js', {
        credentials: 'same-origin',
        cache: 'no-store'
      });

      if (!response.ok) {
        throw new Error(`Cart request failed: ${response.status}`);
      }

      return response.json();
    }

    getCartSignature(cart) {
      if (!cart || !Array.isArray(cart.items)) return '';
      return cart.items.map((item) => `${item.key}:${item.quantity}`).join('|');
    }

    async checkCartChangedAndReload() {
      try {
        const cart = await this.fetchCart();
        const signature = this.getCartSignature(cart);

        if (this.lastCartSignature === '') {
          this.lastCartSignature = signature;
          return;
        }

        if (signature !== this.lastCartSignature) {
          this.debug('Cart signature changed', {
            before: this.lastCartSignature,
            after: signature
          });

          this.lastCartSignature = signature;

          this.queueLoad(80);

          setTimeout(() => {
            if (this.isConnected) {
              this.queueLoad(420);
            }
          }, 420);
        }
      } catch (error) {
        this.debug('Cart polling failed', error);
      }
    }

    startCartPolling() {
      if (this.cartPollInterval) {
        clearInterval(this.cartPollInterval);
      }

      this.checkCartChangedAndReload();

      this.cartPollInterval = setInterval(() => {
        this.checkCartChangedAndReload();
      }, 900);

      const nudge = () => {
        if (this.fastPollTimeout) {
          clearTimeout(this.fastPollTimeout);
        }

        this.fastPollTimeout = setTimeout(() => {
          this.checkCartChangedAndReload();
        }, 180);
      };

      this.addEventListener('click', nudge, true);
      document.addEventListener('visibilitychange', () => {
        if (!document.hidden) nudge();
      });
      window.addEventListener('pageshow', nudge);
      window.addEventListener('focus', nudge);
    }

    showLoadingState() {
      const track = this.querySelector('.dw-cart-recommendations__track');
      if (!track) return;

      this.classList.add('is-loading');

      track.innerHTML = `
        <div class="dw-cart-recommendations__skeleton" aria-hidden="true">
          <div class="dw-cart-recommendations__skeleton-image"></div>
          <div class="dw-cart-recommendations__skeleton-body">
            <div class="dw-cart-recommendations__skeleton-line dw-cart-recommendations__skeleton-line--medium"></div>
            <div class="dw-cart-recommendations__skeleton-line dw-cart-recommendations__skeleton-line--short"></div>
            <div class="dw-cart-recommendations__skeleton-line dw-cart-recommendations__skeleton-line--button"></div>
          </div>
        </div>
        <div class="dw-cart-recommendations__skeleton" aria-hidden="true">
          <div class="dw-cart-recommendations__skeleton-image"></div>
          <div class="dw-cart-recommendations__skeleton-body">
            <div class="dw-cart-recommendations__skeleton-line dw-cart-recommendations__skeleton-line--medium"></div>
            <div class="dw-cart-recommendations__skeleton-line dw-cart-recommendations__skeleton-line--short"></div>
            <div class="dw-cart-recommendations__skeleton-line dw-cart-recommendations__skeleton-line--button"></div>
          </div>
        </div>
      `;
    }

    hideLoadingState() {
      this.classList.remove('is-loading');
    }

    async loadRecommendations() {
      const productId = (this.dataset.productId || '').trim();
      const fetchSectionId = (this.dataset.fetchSectionId || '').trim();
      const intent = (this.dataset.intent || 'complementary').trim();
      const url = (this.dataset.url || '').trim();
      const maxPriceEnabled = this.dataset.enableMaxPrice === 'true';
      const maxPrice = parseFloat(this.dataset.maxPrice || '0');
      const limit = parseInt(this.dataset.limit || '4', 10);

      const track = this.querySelector('.dw-cart-recommendations__track');
      if (!track || !fetchSectionId || !url || this.isLoading) return;

      this.debug('loadRecommendations()', {
        productId,
        intent,
        fetchSectionId,
        limit,
        maxPriceEnabled,
        maxPrice
      });

      this.isLoading = true;
      this.showLoadingState();

      try {
        if (!productId) {
          track.innerHTML = '';
          this.style.display = 'none';
          this.classList.remove('dw-cart-recommendations--single');
          this.hideLoadingState();
          return;
        }

        const cart = await this.fetchCart();
        this.lastCartSignature = this.getCartSignature(cart);

        const cartProductIds = new Set(cart.items.map((item) => String(item.product_id)));

        const requestUrl = `${url}&product_id=${productId}&section_id=${fetchSectionId}&intent=${intent}`;
        const response = await fetch(requestUrl, {
          credentials: 'same-origin',
          cache: 'no-store'
        });

        if (!response.ok) {
          throw new Error(`Recommendations request failed: ${response.status}`);
        }

        const htmlText = await response.text();
        const wrapper = document.createElement('div');
        wrapper.innerHTML = htmlText;

        const fetchedRoot = wrapper.querySelector('[data-dw-cart-recommendations-root]');
        if (!fetchedRoot) {
          throw new Error('Recommendations root not found');
        }

        let cards = Array.from(fetchedRoot.querySelectorAll('.dw-cart-recommendations__card'));

        cards = cards.filter((card) => {
          const cardProductId = String(card.dataset.productId || '');
          return cardProductId && !cartProductIds.has(cardProductId);
        });

        if (maxPriceEnabled && maxPrice > 0) {
          const maxPriceCents = Math.round(maxPrice * 100);
          cards = cards.filter((card) => {
            const price = parseInt(card.dataset.productPrice || '0', 10);
            return price <= maxPriceCents;
          });
        }

        cards = cards.slice(0, limit);

        track.innerHTML = '';

        if (cards.length === 0) {
          this.style.display = 'none';
          this.classList.remove('dw-cart-recommendations--single');
          this.hideLoadingState();
          return;
        }

        this.style.display = '';
        this.classList.toggle('dw-cart-recommendations--single', cards.length === 1);

        cards.forEach((card) => track.appendChild(card));

        this.initSlider();
        this.attachCardEvents();

        requestAnimationFrame(() => this.updateSlider());
        setTimeout(() => this.updateSlider(), 50);
        setTimeout(() => this.updateSlider(), 200);
      } catch (error) {
        console.error('DW cart recommendations error:', error);
      } finally {
        this.isLoading = false;
        this.hideLoadingState();
      }
    }

    formatMoney(cents) {
      const amount = cents / 100;
      const locale = document.documentElement.lang || 'de';
      const currency = document.documentElement.getAttribute('data-currency') || 'EUR';

      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency
      }).format(amount);
    }

    initSlider() {
      this.teardownSwipe();

      this.track = this.querySelector('.dw-cart-recommendations__track');
      this.trackOuter = this.querySelector('.dw-cart-recommendations__track-outer');
      this.prevBtn = this.querySelector('.dw-cart-recommendations__nav--prev');
      this.nextBtn = this.querySelector('.dw-cart-recommendations__nav--next');
      this.cards = this.querySelectorAll('.dw-cart-recommendations__card');

      if (!this.track || !this.trackOuter || !this.prevBtn || !this.nextBtn || this.cards.length === 0) {
        return;
      }

      this.currentIndex = 0;

      const sliderWrapper = this.querySelector('.dw-cart-recommendations__slider-wrapper');

      if (sliderWrapper && window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
        sliderWrapper.onmouseenter = () => {
          this.isHovering = true;
          this.updateSlider();
        };

        sliderWrapper.onmouseleave = () => {
          this.isHovering = false;
          this.updateSlider();
        };
      }

      this.prevBtn.onclick = () => {
        this.hasInteracted = true;
        this.classList.add('dw-cart-recommendations--interacted');

        if (this.currentIndex > 0) {
          this.currentIndex--;
        } else {
          this.currentIndex = 0;
        }
        this.updateSlider();
      };

      this.nextBtn.onclick = () => {
        this.hasInteracted = true;
        this.classList.add('dw-cart-recommendations--interacted');

        const maxIndex = this.getMaxIndex();
        if (this.currentIndex < maxIndex) {
          this.currentIndex++;
        } else {
          this.currentIndex = maxIndex;
        }
        this.updateSlider();
      };

      this.setupSwipe();
      this.updateSlider();
    }

    getCardStepWidth() {
      if (!this.cards || !this.cards.length) return 0;
      const gap = parseInt(this.dataset.cardGap || '0', 10);
      return this.cards[0].offsetWidth + gap;
    }

    getVisibleCardsCount() {
      if (this.classList.contains('dw-cart-recommendations--single')) {
        return 1;
      }

      const stepWidth = this.getCardStepWidth();
      if (!this.trackOuter || !stepWidth) return 1;

      const visible = Math.floor(this.trackOuter.clientWidth / stepWidth);
      return Math.max(1, visible);
    }

    getMaxIndex() {
      if (!this.cards || !this.cards.length) return 0;
      return Math.max(0, this.cards.length - this.getVisibleCardsCount());
    }

    getCurrentTranslateX() {
      const stepWidth = this.getCardStepWidth();
      return -(this.currentIndex * stepWidth);
    }

    updateSlider() {
      if (!this.track || !this.cards || !this.cards.length) return;

      const stepWidth = this.getCardStepWidth();
      const maxIndex = this.getMaxIndex();

      if (this.currentIndex > maxIndex) {
        this.currentIndex = maxIndex;
      }
      if (this.currentIndex < 0) {
        this.currentIndex = 0;
      }

      this.track.style.transition = 'transform 0.3s ease';
      this.track.style.transform = `translateX(-${this.currentIndex * stepWidth}px)`;

      const onlyOneCard = this.cards.length <= 1 || this.classList.contains('dw-cart-recommendations--single');
      const showControls = !onlyOneCard;

      this.classList.toggle('dw-cart-recommendations--interacted', this.hasInteracted);

      if (this.prevBtn) {
        const hidePrev = !showControls || this.currentIndex === 0;
        this.prevBtn.classList.toggle('is-hidden', hidePrev);
      }

      if (this.nextBtn) {
        const hideNext = !showControls || this.currentIndex >= maxIndex;
        this.nextBtn.classList.toggle('is-hidden', hideNext);
      }
    }

    setupSwipe() {
      if (!this.trackOuter || !this.track) return;

      this.onPointerDown = (event) => {
        if (event.pointerType === 'mouse' && event.button !== 0) return;
        if (event.target.closest('a, button, select, input, textarea, label')) return;
        if (!this.cards || this.cards.length <= 1) return;

        this.drag.active = true;
        this.drag.pointerId = event.pointerId;
        this.drag.startX = event.clientX;
        this.drag.currentX = event.clientX;
        this.drag.baseTranslate = this.getCurrentTranslateX();
        this.drag.moved = false;

        this.track.style.transition = 'none';
        this.trackOuter.setPointerCapture?.(event.pointerId);
      };

      this.onPointerMove = (event) => {
        if (!this.drag.active || event.pointerId !== this.drag.pointerId) return;

        this.drag.currentX = event.clientX;
        const deltaX = this.drag.currentX - this.drag.startX;

        if (Math.abs(deltaX) > 4) {
          this.drag.moved = true;
        }

        const maxTranslate = 0;
        const minTranslate = -(this.getMaxIndex() * this.getCardStepWidth());
        let nextTranslate = this.drag.baseTranslate + deltaX;

        if (nextTranslate > maxTranslate + 30) nextTranslate = maxTranslate + 30;
        if (nextTranslate < minTranslate - 30) nextTranslate = minTranslate - 30;

        this.track.style.transform = `translateX(${nextTranslate}px)`;
      };

      this.onPointerUp = (event) => {
        if (!this.drag.active || event.pointerId !== this.drag.pointerId) return;

        const deltaX = this.drag.currentX - this.drag.startX;
        const threshold = Math.min(60, this.getCardStepWidth() * 0.18);

        this.drag.active = false;
        this.trackOuter.releasePointerCapture?.(event.pointerId);

        if (Math.abs(deltaX) > 8) {
          this.hasInteracted = true;
          this.classList.add('dw-cart-recommendations--interacted');
        }

        if (deltaX <= -threshold) {
          this.currentIndex = Math.min(this.currentIndex + 1, this.getMaxIndex());
        } else if (deltaX >= threshold) {
          this.currentIndex = Math.max(this.currentIndex - 1, 0);
        }

        this.updateSlider();
      };

      this.onPointerCancel = () => {
        if (!this.drag.active) return;
        this.drag.active = false;
        this.updateSlider();
      };

      this.trackOuter.addEventListener('pointerdown', this.onPointerDown);
      this.trackOuter.addEventListener('pointermove', this.onPointerMove);
      this.trackOuter.addEventListener('pointerup', this.onPointerUp);
      this.trackOuter.addEventListener('pointercancel', this.onPointerCancel);
      this.trackOuter.addEventListener('lostpointercapture', this.onPointerCancel);
    }

    teardownSwipe() {
      if (!this.trackOuter) return;

      if (this.onPointerDown) {
        this.trackOuter.removeEventListener('pointerdown', this.onPointerDown);
        this.trackOuter.removeEventListener('pointermove', this.onPointerMove);
        this.trackOuter.removeEventListener('pointerup', this.onPointerUp);
        this.trackOuter.removeEventListener('pointercancel', this.onPointerCancel);
        this.trackOuter.removeEventListener('lostpointercapture', this.onPointerCancel);
      }
    }

    updateCardPrice(card, variantId, variantData) {
      const priceEl = card.querySelector('[data-price-display]');
      const compareEl = card.querySelector('[data-compare-display]');
      const unitEl = card.querySelector('[data-unit-display]');
      const atcBtn = card.querySelector('.dw-cart-recommendations__card-atc');

      if (priceEl) {
        priceEl.innerHTML = variantData.priceFormatted || '';
      }

      if (compareEl) {
        if (variantData.compare_at_price && variantData.compare_at_price > variantData.price && variantData.compareFormatted) {
          compareEl.innerHTML = variantData.compareFormatted;
          compareEl.style.display = '';
        } else {
          compareEl.style.display = 'none';
          compareEl.innerHTML = '';
        }
      }

      if (unitEl) {
        const unitSuffix = this.dataset.unitPriceSuffix || '';

        if (variantData.unit_price && variantData.unitPriceFormatted) {
          unitEl.innerHTML = `${variantData.unitPriceFormatted} ${unitSuffix}`.trim();
          unitEl.style.display = '';
        } else {
          unitEl.style.display = 'none';
          unitEl.innerHTML = '';
        }
      }

      if (atcBtn) {
        atcBtn.dataset.variantId = variantId;
        atcBtn.disabled = variantData.available === false;
      }
    }

    async refreshCartDrawerSection() {
      const cartItemsComponent = document.querySelector('cart-items-component');
      const sectionId = cartItemsComponent?.dataset.sectionId;

      if (!sectionId) {
        const response = await fetch('/cart.js', {
          credentials: 'same-origin',
          cache: 'no-store'
        });

        if (!response.ok) return;

        const cart = await response.json();
        this.lastCartSignature = this.getCartSignature(cart);
        return;
      }

      const response = await fetch(`${window.location.pathname}?section_id=${sectionId}`, {
        credentials: 'same-origin',
        cache: 'no-store'
      });

      if (!response.ok) return;

      const htmlText = await response.text();
      const wrapper = document.createElement('div');
      wrapper.innerHTML = htmlText;

      const newCartItemsComponent = wrapper.querySelector('cart-items-component');
      const currentCartItemsComponent = document.querySelector('cart-items-component');

      if (newCartItemsComponent && currentCartItemsComponent) {
        currentCartItemsComponent.innerHTML = newCartItemsComponent.innerHTML;
      }

      const cartResponse = await fetch('/cart.js', {
        credentials: 'same-origin',
        cache: 'no-store'
      });

      if (!cartResponse.ok) return;

      const cart = await cartResponse.json();
      this.lastCartSignature = this.getCartSignature(cart);
    }

    attachCardEvents() {
      this.querySelectorAll('.dw-cart-recommendations__card-variants select').forEach((select) => {
        if (select.dataset.bound) return;

        select.addEventListener('change', () => {
          const selected = select.options[select.selectedIndex];
          const card = select.closest('.dw-cart-recommendations__card');

          this.updateCardPrice(card, selected.value, {
            price: parseInt(selected.dataset.variantPrice || '0', 10),
            compare_at_price: parseInt(selected.dataset.variantCompare || '0', 10),
            unit_price: parseInt(selected.dataset.variantUnitPrice || '0', 10),
            unit_measure: selected.dataset.variantUnitMeasure || '',
            priceFormatted: selected.dataset.variantPriceFormatted || '',
            compareFormatted: selected.dataset.variantCompareFormatted || '',
            unitPriceFormatted: selected.dataset.variantUnitPriceFormatted || '',
            available: selected.dataset.available === 'true'
          });
        });

        select.dataset.bound = 'true';
      });

            this.querySelectorAll('.dw-cart-recommendations__card-atc').forEach((btn) => {
        if (btn.dataset.bound) return;

        const statusEl = btn.querySelector('.dw-cart-recommendations__card-atc-status');

        btn.addEventListener('click', async () => {
          const variantId = btn.dataset.variantId;
          if (!variantId) return;

          btn.disabled = true;
          btn.classList.add('is-feedback');
          if (statusEl) statusEl.textContent = '...';

          try {
            const response = await fetch('/cart/add.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ id: variantId, quantity: 1 })
            });

            if (!response.ok) {
              throw new Error('Add to cart failed');
            }

            if (statusEl) statusEl.textContent = '✓';

            await this.refreshCartDrawerSection();
            this.queueLoad(120);

            setTimeout(() => {
              if (statusEl) statusEl.textContent = '';
              btn.classList.remove('is-feedback');
              btn.disabled = false;
            }, 1200);
          } catch (error) {
            console.error('DW cart recommendations ATC error:', error);
            if (statusEl) statusEl.textContent = '';
            btn.classList.remove('is-feedback');
            btn.disabled = false;
          }
        });

        btn.dataset.bound = 'true';
      });
    }
  }

  customElements.define('dw-cart-recommendations', DwCartRecommendations);
}